import type { RequestOptionsInit } from 'umi-request';
import request from 'umi-request';
import { getQuery } from 'bv-utils';
import type { ListData, ObjectMap, SimpleFunc } from 'bv-typings';

import type { IAeReviewAccountInfo } from '@/services/accountProcess/fb/reviewAccount';
import type { IPolicySight } from '@/services/policyInsight/interface';
import { post, put, read } from '@/utils/request';
import { toggleTime } from '@/utils';
import type { ISignAPI } from '@/services/typings';
import type { OeTokenAPI } from '@/services/administration/oeToken/typings';
import type { BoolEnum } from '@/data';
import type { IFacebookProcessing } from './interface';
import { isValue } from 'bv-validator';
import { isString } from 'lodash';

export enum TaskStatusEnum {
  RUNNING = 'RUNNING', // 打标中
  FAILURE = 'FAILURE',
  SUCCESS = 'SUCCESS',
}
const SUB_INDUSTRY_LENGTH = 3;
const THIRD_INDUSTRY_LENGTH = 5;
/** 落地页与风险项 */
export type PromotableUrlRiskItem = {
  /** 落地页 */
  url: string;
  /** 风险类型 */
  riskType: string;
  /** 风险项 */
  riskItem: string[];
  /** 一级行业标签 */
  tag1: string;
  /** 二、三级行业标签 */
  tag23: string[][];
  /**
   * 链接是否有效，1有效0无效
   * page_valid为0说明落地页抓取失败，没有行业及风险信息
   * */
  pageValid: BoolEnum;
  /**
   * 链接是否安全，1安全0不安全
   * risk_safe为1说明没有风险信息
   */
  riskSafe: BoolEnum;
  /** 任务状态 */
  taskStatus: TaskStatusEnum;
  /** 任务失败原因 */
  taskMsg: string;
};
// 落地页、行业、风险项
export type PromotableUrlRisk = {
  /** 是否有风险，1有0无 */
  isUnsafe: BoolEnum;
  /** 一级行业标签 */
  industryTag1: string[];
  /** 二、三级行业标签 */
  industryTag23: string[];
  /** 落地页与风险项 */
  urlRisk: PromotableUrlRiskItem[];
};

export type IAeTotalAccountInfo = {
  ae?: string;
  aeNote?: string; // ae备注
  aeNoteTime: string; // AE备注时间
  aeAppendInfoTime: string; // 补充开户信息时间
  accountType: string; // 账户类型
  bfDisapprovalUser: string; // 驳回人
  bfDisapprovalTime: string; // 驳回时间
  bfChangeReason: string; // BF驳回原因
  bfDisapprovedReasons: string;
  bfDisapprovalReasons: string; // BF驳回原因
  contact: string; // 联系邮箱
  customerInfoComplete: string; // 信息调研完成
  entityName: string; // 开户主体
  fbUserId: string; // fb用户ID
  fbUserName: string; // fb用户名称
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fbChangeReason: any[]; // fb请求需改原因
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fbDisapprovedReason: any[]; // fb驳回原因
  id: string; // 账户ID
  isUrgent: number; // 是否加急
  isCancel: number; // 是否提交撤回申请
  name: string; // 账户名称
  number: number; // 开户数量
  officialWebsiteUrl: string;
  ourSideEntity?: string; // 我方主体
  paymentName: string | null; // 结算主体
  pushStatus?: number; // 推送状态
  pushReason?: string; // 推送失败原因
  promotableUrls: string[]; // 粉丝页
  promotableAppIds: string[]; // 推广App Ids
  promotablePageIds: string[];
  requestId: string;
  settlementType?: string; // 结算方式
  sale?: number; // 销售
  status: number; // 状态
  time: string; // 申请开户时间
  tokenId: string; // 开户Token
  vertical: string; // 开户类型
  onSuccess?: SimpleFunc;
  bfCheckTime: string;
  fbCheckTime: string;

  settlementLimitStatus: number;
  openAccountLimitStatus: number;
  sourceType: number;
  isSmb: number;
  bmName: string;
  isNewAccountType: number; // 是否新增开户类型 1是 0否
  signal: IPolicySight.Common.PolicyContent;
  sign: ISignAPI.Response.SignListInfo;

  tokenAttr: OeTokenAPI.Common.TokenTypeEnum; // Token属性 专属/公共
} & PromotableUrlRisk;

const getAccountProcessListParams = (requestParams?: ObjectMap) => {
  const params = {
    ...getQuery(),
    ...requestParams,
  };

  let product_tag2: string[] = [];
  let product_tag3: string[] = [];
  // 处理二三级行业标签, 直接筛选是string[][], 从地址栏刷新是string或string[]
  if (Array.isArray(params?.product_tag23)) {
    if (params?.product_tag23?.every((item) => Array.isArray(item))) {
      /**
       * product_tag23: [['102], ['103', '10301']] 格式
       * ==>
       * product_tag2: ['102', '103']
       * product_tag3: ['10301']
       */
      product_tag2 = params?.product_tag23
        // 有三级行业的只取子行业 不要二级行业
        ?.map((item: string[]) => item?.[0])
        ?.filter((item: string) => isValue(item));
      product_tag3 = params?.product_tag23
        ?.map((item: string[]) => item?.[1])
        ?.filter((item: string) => isValue(item));
    } else {
      /**
       * product_tag23: ["101", "103", "10303", "103", "10304"]
       * ==>
       * product_tag2: ['101', "103"]
       * product_tag3: ['10303', "10304"]
       */
      product_tag3 = params?.product_tag23?.filter(
        (item) => item?.length === THIRD_INDUSTRY_LENGTH,
      );
      product_tag2 = params?.product_tag23?.filter((item) => item?.length === SUB_INDUSTRY_LENGTH);
    }
  } else if (isString(params?.product_tag23)) {
    // 单个行业标签 比如 "101" 或者 "10303"
    if (params?.product_tag23?.length === SUB_INDUSTRY_LENGTH) {
      product_tag2 = [params?.product_tag23];
    } else if (params?.product_tag23?.length === THIRD_INDUSTRY_LENGTH) {
      product_tag3 = [params?.product_tag23];
    }
  }

  const newParams = {
    ...toggleTime(toggleTime(params, 'appendTime', 'aeAppendInfoTimeStart', 'aeAppendInfoTimeEnd')),
    // 去重
    product_tag2: Array.from(new Set(product_tag2)),
    product_tag3: Array.from(new Set(product_tag3)),
  };
  delete newParams.product_tag23;

  return newParams;
};

async function getTotalList(requestParams: ObjectMap) {
  const params = getAccountProcessListParams(requestParams);
  const { isConfirm, openAccountLimitType } = params;

  const newParams = {
    ...params,
    isConfirm: isConfirm ? Number(isConfirm) : undefined,
    openAccountLimitType: openAccountLimitType ? Number(openAccountLimitType) : undefined,
    promotableUrls: Array.isArray(params?.promotableUrls)
      ? params?.promotableUrls?.map((item: string) => btoa(item))?.join(',')
      : params?.promotableUrls
      ? btoa(params?.promotableUrls)
      : undefined,
  };

  return read<ListData<IAeTotalAccountInfo>>('user_account/total_entity', newParams);
}

type ISupplementInfoParams = {
  requestIds: string[];
  paymentName: string;
  ourSideEntity: string;
  settlementType: string;
  sale: string;
  accountType: string;
};

const supplementInfo = async (params: ISupplementInfoParams, requestIds?: string[]) => {
  return post('user_account/oe_info_append', { ...params, requestIds }, '补充信息失败');
};

const madhouseSupplementInfo = async (params: ISupplementInfoParams, requestIds?: string[]) => {
  return post('user_account/oe_info_append_md', { ...params, requestIds }, '补充信息失败');
};

type IManualPushParams = {
  accounts: string[];
};

const manualPush = async (params: IManualPushParams) =>
  post('user_account/push_account', params, '手动推送失败');

type IBatchNotesParams = {
  requestIds: string[];
  noteByAe: string;
};

const batchNotes = async (params: IBatchNotesParams) =>
  post('user_account/batch_add_note_by_ae', params, '批量备注失败');

type IBatchRejectParams = {
  requestIds: string[];
  reason: string;
};

const batchReject = async (params: IBatchRejectParams) =>
  post('user_account/update_oe_status', params, '批量驳回失败');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function exportList(query?: any) {
  return request.get('/api/ka_admin/user_account/oe_download', {
    params: toggleTime(query),
    notice: '获取导出列表失败',
    getResponse: true,
    responseType: 'blob',
  } as RequestOptionsInit);
}

export type IUrgentAuditParams = {
  requestIds: string[];
};

const revokeOpenAccount = async (params: IUrgentAuditParams) =>
  post('user_account/cancel_open_account', params, '批量撤销失败');

const expediteOpenAccount = async (params: IUrgentAuditParams) => {
  return post('user_account/urgent_audit', params, '批量加急失败');
};

const cancelExpediteOpenAccount = async (params: IUrgentAuditParams) => {
  return post('user_account/reurgent_audit', params, '批量取消加急失败');
};

const editAeNote = async (id: string, params: string) => {
  return post(`user_account/add_note_by_ae/${id}`, { noteByAe: params }, '编辑AE备注失败');
};

const batchImport = async (params: ObjectMap[]) => {
  return post('user_account/batch_import_rebate_reason', params);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getTotalDetail = async ({ requestId, id }: any) => {
  return read<ListData<IAeReviewAccountInfo>>(
    `user_account/total_entity/${requestId}/${id ?? 0}`,
    undefined,
    '获取详情失败',
  );
};

// 落地页行业列表
const getRiskIndustryList = (params: { level: IFacebookProcessing.Common.IndusrtyLevelEnum }) =>
  read<IFacebookProcessing.Response.GetIndusrtyList[]>(
    'user_account/industry_list',
    params,
    '获取落地页行业列表失败',
    undefined,
    {
      isCamel: false,
      isLower: false,
    },
  );

// 获取标签复核信息
const getReviewInfo = ({ request_id, account_id }: { request_id: string; account_id: string }) => {
  return read<IFacebookProcessing.Response.GetReviewInfo>(
    `user_account/total_entity/tag_info/${request_id}/${account_id ?? 0}`,
    undefined,
    '获取开户标签详情失败',
    undefined,
    {
      isCamel: false,
      isLower: false,
    },
  );
};

// 落地页风险列表
const getRiskList = (params: { level: IFacebookProcessing.Common.IndusrtyLevelEnum }) =>
  read<IFacebookProcessing.Response.GetIndusrtyList[]>(
    'user_account/risk_list',
    params,
    '获取落地页风险列表失败',
    undefined,
    {
      isCamel: false,
      isLower: false,
    },
  );

// 复核标签修改
const checkTag = ({
  request_id,
  account_id,
  params,
}: IFacebookProcessing.Request.CheckTagParams) => {
  return put(
    `user_account/total_entity/tag_info/${request_id}/${account_id ?? 0}`,
    params,
    '复核失败',
    undefined,
    {
      isCamel: false,
      isLower: false,
    },
  );
};

export {
  getTotalList,
  getTotalDetail,
  getAccountProcessListParams,
  supplementInfo,
  manualPush,
  batchNotes,
  batchReject,
  exportList,
  revokeOpenAccount,
  expediteOpenAccount,
  cancelExpediteOpenAccount,
  editAeNote,
  batchImport,
  madhouseSupplementInfo,
  getRiskIndustryList,
  getReviewInfo,
  getRiskList,
  checkTag,
};
